import { t } from '@lingui/macro'
import { motion } from 'framer-motion'
import React from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'

type SubjectBreakdown = {
    key: 'candidates' | 'carePartners'
    title: React.ReactNode
    data: { name: React.ReactNode; value: number; fill: string; percentage?: number }[]
}

/* eslint-disable string-to-lingui/missing-lingui-transformation */
export const STATUS_COLOR_MAP: Record<string, string> = {
    // candidate statuses
    candidate: 'hsl(210, 80%, 35%)',
    in_contact: 'hsl(174, 55%, 40%)',
    registered: 'hsl(200, 70%, 55%)',
    allocated: 'hsl(280, 55%, 55%)',
    record_retrieval: 'hsl(25, 85%, 55%)',
    screen_failure: 'hsl(350, 65%, 55%)',
    undecided: 'hsl(40, 80%, 50%)',
    declined: 'hsl(10, 70%, 50%)',
    unreachable: 'hsl(145, 45%, 45%)',
    duplicate: 'hsl(210, 15%, 75%)',
    // fallback
    unknown: 'hsl(220, 10%, 70%)'
}
/* eslint-enable string-to-lingui/missing-lingui-transformation */

export const getStatusColor = (status: string) => STATUS_COLOR_MAP[status.toLowerCase().replace(/\s+/g, '_')] ?? STATUS_COLOR_MAP.unknown

export const useSubjectBreakdowns = (): SubjectBreakdown[] => {
    /* eslint-disable string-to-lingui/missing-lingui-transformation */
    return [
        {
            key: 'candidates',
            title: t`Candidates Breakdown`,
            data: [
                { name: t`Screened`, value: 520, fill: 'hsl(210, 80%, 35%)' },
                { name: t`Enrolled`, value: 384, fill: 'hsl(174, 55%, 40%)' },
                { name: t`Pending`, value: 280, fill: 'hsl(200, 70%, 55%)' },
                { name: t`Declined`, value: 100, fill: 'hsl(210, 15%, 75%)' }
            ]
        },
        {
            key: 'carePartners',
            title: t`Care Partners Breakdown`,
            data: [
                { name: t`Active`, value: 98, fill: 'hsl(210, 80%, 35%)' },
                { name: t`Onboarding`, value: 32, fill: 'hsl(174, 55%, 40%)' },
                { name: t`Inactive`, value: 18, fill: 'hsl(200, 70%, 55%)' },
                { name: t`Closed`, value: 8, fill: 'hsl(210, 15%, 75%)' }
            ]
        }
    ]
    /* eslint-enable string-to-lingui/missing-lingui-transformation */
}

export const SubjectBreakdownContent = ({ breakdown }: { breakdown: SubjectBreakdown }) => {
    return (
        <div className='flex items-center gap-4'>
            <div className='w-28 h-28'>
                <ResponsiveContainer width='100%' height='100%'>
                    <PieChart>
                        <Pie
                            data={breakdown.data}
                            cx='50%'
                            cy='50%'
                            innerRadius={28}
                            outerRadius={48}
                            paddingAngle={3}
                            dataKey='value'
                            strokeWidth={0}
                        >
                            {breakdown.data.map((entry, index) => (
                                <Cell key={index} fill={entry.fill} />
                            ))}
                        </Pie>
                        {/* eslint-disable string-to-lingui/missing-lingui-transformation */}
                        <Tooltip
                            contentStyle={{
                                backgroundColor: 'hsl(215, 60%, 18%)',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'white',
                                fontSize: '12px'
                            }}
                            itemStyle={{ color: 'white' }}
                            labelStyle={{ color: 'white' }}
                        />
                        {/* eslint-enable string-to-lingui/missing-lingui-transformation */}
                    </PieChart>
                </ResponsiveContainer>
            </div>
            <div className='flex-1 space-y-2'>
                {breakdown.data.map((item, index) => (
                    <div key={index} className='flex items-center justify-between text-xs'>
                        <div className='flex items-center gap-2'>
                            <span className='w-2.5 h-2.5 rounded-full' style={{ backgroundColor: item.fill }} />
                            <span className='text-muted-foreground'>
                                {typeof item.name === 'string' ? item.name.replace(/^./, (ch) => ch.toUpperCase()) : item.name}
                            </span>
                        </div>
                        <span className='font-semibold text-card-foreground'>
                            {item.value.toLocaleString()}
                            {item.percentage !== undefined && <span className='text-gray-500'>{` (${item.percentage.toFixed(2)}%)`}</span>}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    )
}

const SubjectCharts = () => {
    const subjectBreakdowns = useSubjectBreakdowns()
    return (
        <div className='grid grid-cols-1 md:grid-cols-3 gap-5'>
            {subjectBreakdowns.map((subject, i) => (
                <motion.div
                    key={subject.key}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + i * 0.1, duration: 0.4 }}
                    className='bg-card rounded-lg p-5 card-shadow border border-border'
                >
                    <h3 className='text-sm font-semibold text-card-foreground mb-4'>{subject.title}</h3>
                    <SubjectBreakdownContent breakdown={subject} />
                </motion.div>
            ))}
        </div>
    )
}

export default SubjectCharts
